import {useLayoutEffect, useRef, useState} from 'react'

/**
 * Measure the distance in pixels from the given element's outer-left/right
 * edges to the viewport's left/right edges, so a child can break out to the
 * viewport via `margin-left: -leftPx`, `margin-right: -rightPx`. Re-measures
 * on resize.
 *
 * Using measured pixel values (not `calc(50% - 50vw)`) avoids miscalcs from
 * vertical scrollbars, ancestor padding, and rounded layouts. Returns
 * `{leftPx, rightPx, scrollbarPx}` (zero when SSR / not yet measured).
 *
 * `scrollbarPx` is the width of the vertical scrollbar (`window.innerWidth -
 * documentElement.clientWidth`). It's a global viewport property (independent
 * of this element's position) so it can safely be consumed even by a
 * `calc(50% - 50vw)`-bled element to cancel the scrollbar overshoot that the
 * `100vw` unit introduces.
 *
 * @param {unknown} refreshKey - Changes when async carousel content appears.
 * @returns {{ref: React.RefObject<HTMLElement>, leftPx: number, rightPx: number, scrollbarPx: number}}
 */
export const useViewportBleed = (refreshKey) => {
    const ref = useRef(null)
    const [bleed, setBleed] = useState({leftPx: 0, rightPx: 0, scrollbarPx: 0})

    useLayoutEffect(() => {
        const node = ref.current
        if (!node || typeof window === 'undefined') return undefined

        const measure = () => {
            const rect = node.getBoundingClientRect()
            /* `documentElement.clientWidth` excludes the vertical scrollbar
             * (unlike `window.innerWidth`), matching what `100%` resolves
             * to for a body-width-stretched ancestor. Using it for the
             * right edge keeps the carousel's right boundary aligned with
             * the visible viewport edge rather than overshooting under the
             * scrollbar gutter. */
            const viewportWidth = document.documentElement.clientWidth
            const leftPx = Math.max(0, Math.round(rect.left))
            const rightPx = Math.max(0, Math.round(viewportWidth - rect.right))
            /* `100vw` resolves to `window.innerWidth` (INCLUDES the vertical
             * scrollbar), while `100%`/`50%` resolve against the scrollbar-free
             * `clientWidth`. The difference is the scrollbar gutter — exposed
             * here so a `calc(50% - 50vw)` fluid-bleed can add `scrollbarPx/2`
             * back to land exactly on the visible viewport edges. */
            const scrollbarPx = Math.max(0, Math.round(window.innerWidth - viewportWidth))
            setBleed((prev) =>
                prev.leftPx === leftPx &&
                prev.rightPx === rightPx &&
                prev.scrollbarPx === scrollbarPx
                    ? prev
                    : {leftPx, rightPx, scrollbarPx}
            )
        }

        /* Measure from the ResizeObserver only: its callback runs after layout, so the
         * rect read is free, and observe() fires once on start. Reading synchronously
         * here (inside React's commit) forced a full reflow on every mount. */
        const ro =
            typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
        if (ro) ro.observe(node)
        else measure()
        window.addEventListener('resize', measure)

        return () => {
            window.removeEventListener('resize', measure)
            if (ro) ro.disconnect()
        }
    }, [refreshKey])

    return {ref, ...bleed}
}
